import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Stack from 'components/core/Stack';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import Typography from 'components/core/Typography';
import Checkbox from 'components/core/Checkbox';
import LookupTableInput from 'components/LookupTableInput';

import personActions from 'actions/personActions';

const PersonForm = ({ onSubmit, initialValues }) => {
    const dispatch = useDispatch();
    const options = useSelector(({ lookupTable }) => lookupTable.options);
    const [person, setPerson] = useState({
        firstName: initialValues?.get?.('firstName') || initialValues?.firstName || '',
        lastName: initialValues?.get?.('lastName') || initialValues?.lastName || '',
        email: initialValues?.get?.('email') || initialValues?.email || '',
        deceased: initialValues?.get?.('deceased') || initialValues?.deceased || false,
        subjects: initialValues?.get?.('subjects') || initialValues?.subjects || [],
        alternativeNames: initialValues?.get?.('alternativeNames') || initialValues?.alternativeNames || []
    });
    const [errors, setErrors] = useState({});

    const handleSave = async () => {
        setErrors({});

        if (!person.firstName) {
            setErrors(prev => ({ ...prev, firstName: 'First name is required' }));
            return;
        }
        if (!person.lastName) {
            setErrors(prev => ({ ...prev, lastName: 'Last name is required' }));
            return;
        }
        

        try {
            if (initialValues?.id || initialValues?.get?.('id')) {
                const personId = initialValues.id || initialValues.get('id');
                const personData = {
                    objectId: personId,
                    firstName: person.firstName,
                    lastName: person.lastName,
                    email: person.email,
                    deceased: person.deceased
                };
                
                const savedPerson = await dispatch(personActions.save(personData));
                
                await dispatch(personActions.deletePersonExpertise(personId));
                
                const expertisePromises = person.subjects.map(subject => {
                    const expertiseData = {
                        person: personId,
                        subject: subject.id
                    };
                    return dispatch(personActions.saveExpertise(expertiseData));
                });
                
                await Promise.all(expertisePromises);
                await dispatch(personActions.list());
                onSubmit(savedPerson.value);
            } else {
                const existingPersonResponse = await dispatch(personActions.get({ 
                    firstName: person.firstName,
                    lastName: person.lastName,
                    email: person.email 
                }));
                const existingPerson = existingPersonResponse.value;
                if (existingPerson) {
                const personSynonymsResponse = await dispatch(personActions.getAllNamesOfPerson(existingPerson.id))
                const personSynonyms = personSynonymsResponse.value;
                const primaryName = `${existingPerson.firstName} ${existingPerson.lastName}`;
                const plainTextExistingSynonyms = personSynonyms.map(synonym => synonym.get('name'));
                const nonPrimaryExistingNames = plainTextExistingSynonyms.filter(name => name !== primaryName);
                const newAlternateNames = person.alternativeNames.filter(name => !nonPrimaryExistingNames.includes(name));
                await Promise.all(newAlternateNames.map((name) => {
                    return dispatch(personActions.savePersonName({
                        person: existingPerson.id,
                        name
                    }));
                }))
                await dispatch(personActions.list());
                onSubmit(existingPerson);
            } else {
                 
                const personData = {
                     firstName: person.firstName,
                     lastName: person.lastName,
                     email: person.email,
                     deceased: person.deceased,
                     alternativeNames: person.alternativeNames.filter(name => name)
                };
     
                const savedPerson = await dispatch(personActions.save(personData));
                 
                const expertisePromises = person.subjects.map(subject => {
                    const expertiseData = {
                        person: savedPerson.value.id,
                        subject: subject.id
                     };
                    return dispatch(personActions.saveExpertise(expertiseData));
                 });
     
                await Promise.all(expertisePromises);
                await dispatch(personActions.list());
                onSubmit(savedPerson.value);
            }
            }
        } catch (err) {
            setErrors({ submit: err.message });
        }
    };

    const handleFieldChange = (field, value) => {
        setPerson(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Stack spacing={4}>

            <Input
                label="First Name"
                value={person.firstName}
                onChange={(value) => handleFieldChange('firstName', value)}
                error={errors.firstName}
                required
            />

            <Input
                label="Last Name"
                value={person.lastName}
                onChange={(value) => handleFieldChange('lastName', value)}
                error={errors.lastName}
                required
            />
            {person.alternativeNames.map((name, index) => (
                <Input
                    key={index}
                    label={`Alternative Name ${index + 1}`}
                    value={name}
                    onChange={(value) => {
                        const newNames = [...person.alternativeNames];
                        newNames[index] = value;
                        handleFieldChange('alternativeNames', newNames);
                    }}
                    error={errors.names && errors.names[index]}
                    required
                />
            ))}
            <Button onClick={() => handleFieldChange('alternativeNames', [...person.alternativeNames, null])}>
                Add Alternative Name
            </Button>
            <Input
                label="Email"
                value={person.email}
                onChange={(value) => handleFieldChange('email', value)}
                error={errors.email}
            />

            <Checkbox
                label="Deceased"
                checked={person.deceased}
                onChange={(e) => handleFieldChange('deceased', e.target.checked)}
            />

            <LookupTableInput
                id="subjects"
                label="Subject Expertise"
                value={person.subjects}
                onChange={(value) => handleFieldChange('subjects', value)}
                options={options.subjects}
                getOptionLabel={(option) => `${option?.get('label')} (${option?.get('shortCode')})`}
                error={errors.subjects}
                multiple
            />

            {errors.submit && (
                <Typography style={{color: 'red'}}>{errors.submit}</Typography>
            )}

            <Button onClick={handleSave}>
                {initialValues ? 'Update' : 'Create'} Person
            </Button>
        </Stack>
    );
};

PersonForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object
};

export default PersonForm;